import { ref, onUnmounted } from "vue";
import type {
  FirestoreDataConverter,
  FirestoreError,
} from "firebase/firestore";
import type { Comment } from "@kekkle/shared/types/fields/comment/comment";
import {
  subscribeToComments as subscribeToCommentsService,
  postComment as postCommentService,
} from "@/helpers/firestore/CommentService";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { setErrorToast } from "@/helpers/notifications/toast";

type UseCommentsOptions = {
  groupId: string;
  itemId: string;
  collectionName: string;
  converter: FirestoreDataConverter<any>;
  incrementField?: string;
  /** See `stampUpdatedAt` in CommentService. */
  stampUpdatedAt?: boolean;
  authorUuid: string;
};

export function useComments(options: UseCommentsOptions) {
  const comments = ref<Comment[]>([]);
  const loading = ref(true);
  const posting = ref(false);
  const fetchError = ref<string | null>(null);

  // Subscribe to comments with real-time listener
  const unsubscribe = subscribeToCommentsService(
    {
      groupId: options.groupId,
      itemId: options.itemId,
      collectionName: options.collectionName,
      converter: options.converter,
    },
    {
      onComments: (newComments) => {
        comments.value = newComments;
        loading.value = false;
      },
      onError: (error) => {
        fetchError.value = handleFirestoreException({
          e: error as FirestoreError,
        });
        loading.value = false;
      },
    },
  );

  onUnmounted(() => {
    unsubscribe();
  });

  async function postComment(commentText: string): Promise<void> {
    posting.value = true;
    try {
      await postCommentService({
        groupId: options.groupId,
        itemId: options.itemId,
        collectionName: options.collectionName,
        commentText,
        authorUuid: options.authorUuid,
        incrementField: options.incrementField,
        stampUpdatedAt: options.stampUpdatedAt,
      });
      // onSnapshot listener will pick up the new comment automatically
    } catch (e) {
      const message = handleFirestoreException({ e: e as FirestoreError });
      setErrorToast({ message });
      throw e;
    } finally {
      posting.value = false;
    }
  }

  return {
    comments,
    loading,
    posting,
    fetchError,
    postComment,
  };
}
